import { X } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui'
import { ImagePreview } from '@/components/common/ImagePreview'
import { cn } from '@/lib/utils'
import type {
  ImportedImage,
  ToolCell,
  ToolRow,
} from '@/utils/tool-management/tool-management.util'
import { findImageByLabel } from '@/utils/tool-management/tool-management.util'
import { CellEnum } from '@/enums/cell.enum'
import { isValidRow } from '@/utils/tool-management/row-validation.util'

interface PreviewTableProps {
  items: ToolRow[]
  imageMap: Record<string, ImportedImage>
  onImageClick: (imageLabel: string) => void
  onImageRemove: (imageLabel: string, e: React.MouseEvent) => void
}

export function PreviewTable({
  items,
  imageMap,
  onImageClick,
  onImageRemove,
}: PreviewTableProps) {
  const { t } = useTranslation()

  return (
    <div className='overflow-x-auto'>
      <table className='w-full text-sm border rounded-lg border-collapse min-w-[800px]'>
        <thead className='bg-gray-50 border-b'>
          <tr>
            <th className='px-4 py-3 text-left font-medium text-gray-700'>
              {t('toolManagement.pn')}
            </th>
            <th className='px-4 py-3 text-left font-medium text-gray-700'>
              {t('toolManagement.chineseName')}
            </th>
            <th className='px-4 py-3 text-left font-medium text-gray-700'>
              {t('toolManagement.description')}
            </th>
            <th className='px-4 py-3 text-center font-medium text-gray-700'>
              {t('toolManagement.quantity')}
            </th>
            <th className='px-4 py-3 text-center font-medium text-gray-700'>
              {t('toolManagement.image')}
            </th>
          </tr>
        </thead>
        <tbody>
          {items.map((row: ToolRow, index: number) => {
            const pnValue = row[CellEnum.Pn] as ToolCell
            const chineseNameValue = row[CellEnum.ChineseName] as ToolCell
            const descriptionValue = row[CellEnum.Description] as ToolCell
            const quantityValue = row[CellEnum.Quantity] as ToolCell

            const hasInvalidValue = !isValidRow(row)

            return (
              <tr
                key={index}
                className={cn(
                  hasInvalidValue
                    ? 'bg-[#FFF0EF]'
                    : index % 2 === 0
                      ? 'bg-white'
                      : 'bg-gray-50'
                )}
              >
                <td className='px-4 py-3 text-gray-900 border-b border-l-0 border-r-0 border-t-0 border-[#E9EAEB]'>
                  {pnValue ?? '-'}
                </td>
                <td className='px-4 py-3 text-gray-900 border-b border-l-0 border-r-0 border-t-0 border-[#E9EAEB]'>
                  {chineseNameValue ?? '-'}
                </td>
                <td className='px-4 py-3 text-gray-600 border-b border-l-0 border-r-0 border-t-0 border-[#E9EAEB]'>
                  {descriptionValue ?? '-'}
                </td>
                <td className='px-4 py-3 text-center text-gray-900 border-b border-l-0 border-r-0 border-t-0 border-[#E9EAEB]'>
                  {quantityValue ?? '-'}
                </td>
                <td className='px-4 py-3 text-center border-b border-l-0 border-r-0 border-t-0 border-[#E9EAEB]'>
                  {(() => {
                    const imageLabel = row[CellEnum.Image] as ToolCell
                    const image = findImageByLabel(
                      imageMap,
                      imageLabel as string
                    )

                    if (image?.presignedUrl) {
                      return (
                        <div className='flex justify-center items-center gap-2'>
                          <div
                            className='relative group cursor-pointer'
                            onClick={() => onImageClick(imageLabel as string)}
                            title='Click to update image'
                          >
                            <ImagePreview
                              url={image.presignedUrl}
                              name={image.name}
                              width={40}
                              height={40}
                            />
                            <Button
                              type='button'
                              onClick={e =>
                                onImageRemove(imageLabel as string, e)
                              }
                              className='absolute top-0 right-0 bg-[#3D2E16] text-white hover:bg-[#3D2E16] transition-colors shadow-sm p-[2px] h-auto w-auto rounded-full'
                              title='Remove image'
                            >
                              <X className='h-3 w-3' />
                            </Button>
                          </div>
                        </div>
                      )
                    }

                    return (
                      <span
                        className='text-blue-600 cursor-pointer'
                        onClick={() => onImageClick(imageLabel as string)}
                        title='Click to add image'
                      >
                        +
                      </span>
                    )
                  })()}
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}
